import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/dashboard/DashboardIcons';
import { toast } from '../../../lib/toast';
import { API, auth, Paginator, errMsg, LEVEL_LABELS, EXERCISE_STATUS_LABELS, StudioEmpty } from './shared';

const SCOPES = [
  { key: 'mine', label: 'تماريني', icon: 'clipboard' },
  { key: 'organization', label: 'تابع للمنشأة', icon: 'building' },
  { key: 'shared', label: 'مشارك معي', icon: 'users' },
  { key: 'public', label: 'مكتبة تسيار', icon: 'trophy' },
  { key: 'favorites', label: 'المفضلة', icon: 'star' },
];

const LEVEL_COLORS = { beginner: 'bg-slate-100 text-slate-600', intermediate: 'bg-blue-50 text-blue-700', advanced: 'bg-orange-50 text-orange-700' };
const TYPE_ICONS = {
  warmup: 'bolt', fitness: 'bolt', passing: 'flag', shooting: 'flag', possession: 'grid', defending: 'lock',
  attacking: 'bolt', speed_agility: 'bolt', tactical: 'layers', goalkeeping: 'whistle', recovery: 'heart', transition: 'refresh',
};

export default function ExerciseLibraryPage() {
  const [scope, setScope] = useState('mine');
  const [search, setSearch] = useState('');
  const [exerciseType, setExerciseType] = useState('');
  const [level, setLevel] = useState('');
  const [status, setStatus] = useState('');
  const [types, setTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/coach-studio/config`).then((r) => setTypes(r.data.exercise_types || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/coach-studio/exercises`, {
        ...auth(), params: { scope, search: search || undefined, exercise_type: exerciseType || undefined, level: level || undefined, status: scope === 'mine' ? (status || undefined) : undefined, page, page_size: 12 },
      });
      setItems(res.data.items); setTotal(res.data.total);
    } catch (e) {
      setError(errMsg(e, 'تعذر تحميل التمارين'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [scope, page]);
  useEffect(() => { setPage(1); }, [scope, search, exerciseType, level, status]);

  const doDuplicate = async (id) => {
    try {
      const res = await axios.post(`${API}/coach-studio/exercises/${id}/duplicate`, {}, auth());
      toast.success('تم نسخ التمرين إلى تمارينك');
      navigate(`/coach/studio/exercises/${res.data.id}`);
    } catch (e) { toast.error(errMsg(e, 'تعذر النسخ')); }
  };

  const toggleFavorite = async (item) => {
    try {
      if (item.is_favorite) await axios.delete(`${API}/coach-studio/exercises/${item.id}/favorite`, auth());
      else await axios.post(`${API}/coach-studio/exercises/${item.id}/favorite`, {}, auth());
      load();
    } catch (e) { toast.error(errMsg(e, 'تعذر التحديث')); }
  };

  const archiveExercise = async (id) => {
    try {
      await axios.put(`${API}/coach-studio/exercises/${id}/status?status=archived`, {}, auth());
      toast.success('تم أرشفة التمرين');
      load();
    } catch (e) { toast.error(errMsg(e, 'تعذر الأرشفة')); }
  };

  const unarchiveExercise = async (id) => {
    try {
      await axios.put(`${API}/coach-studio/exercises/${id}/status?status=ready`, {}, auth());
      toast.success('تم إلغاء الأرشفة');
      load();
    } catch (e) { toast.error(errMsg(e, 'تعذر إلغاء الأرشفة')); }
  };

  return (
    <div className="space-y-5" data-testid="exercise-library-page">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-l from-[#0f2a4a] to-[#0d9488] p-5 text-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
              <Icon name="dumbbell" className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-bold">مكتبة التمارين</h2>
              <p className="text-xs text-white/80">أنشئ تمارينك أو استلهم من مكتبة تسيار الجاهزة</p>
            </div>
          </div>
          <button data-testid="create-exercise-btn" onClick={() => navigate('/coach/studio/exercises/new')} className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#0f2a4a] transition-colors hover:bg-white/90">
            <Icon name="plus" className="w-4 h-4" /> تمرين جديد
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1.5" data-testid="exercise-scope-tabs">
        {SCOPES.map((s) => (
          <button key={s.key} onClick={() => setScope(s.key)} data-testid={`exercise-scope-${s.key}`}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${scope === s.key ? 'bg-[#0d9488] text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}>
            <Icon name={s.icon} className="h-3.5 w-3.5" />
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="relative">
          <Icon name="search" className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="ابحث عن تمرين..."
            className="w-56 rounded-lg border border-slate-200 py-2 pl-3 pr-9 text-sm outline-none focus:border-teal-400" data-testid="exercise-search-input" />
        </div>
        <select value={exerciseType} onChange={(e) => setExerciseType(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="exercise-type-filter">
          <option value="">كل الأنواع</option>
          {types.map((t) => <option key={t.value} value={t.value}>{t.label_ar}</option>)}
        </select>
        <select value={level} onChange={(e) => setLevel(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="exercise-level-filter">
          <option value="">كل المستويات</option>
          {Object.entries(LEVEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {scope === 'mine' && (
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="exercise-status-filter">
            <option value="">كل الحالات (باستثناء المؤرشف)</option>
            {Object.entries(EXERCISE_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        )}
        <button onClick={load} className="rounded-lg bg-[#0f2a4a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16233b]" data-testid="exercise-search-btn">بحث</button>
      </div>

      {loading ? <div className="p-8 text-center text-sm text-slate-400" data-testid="exercise-library-loading">جاري التحميل...</div> :
        error ? <div className="p-8 text-center text-sm text-red-500" data-testid="exercise-library-error">{error}</div> :
        items.length === 0 ? <StudioEmpty text="لا توجد تمارين في هذا القسم" icon={<Icon name="dumbbell" className="w-6 h-6" />} /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="exercise-grid">
          {items.map((ex) => (
            <div key={ex.id} className="group rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all hover:-translate-y-0.5 hover:border-teal-200 hover:shadow-lg" data-testid={`exercise-card-${ex.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                    <Icon name={TYPE_ICONS[ex.exercise_type] || 'dumbbell'} className="h-4 w-4" />
                  </span>
                  <h4 className="cursor-pointer font-bold text-slate-900 group-hover:text-teal-700" onClick={() => navigate(`/coach/studio/exercises/${ex.id}`)} data-testid={`exercise-title-${ex.id}`}>{ex.title}</h4>
                </div>
                <button onClick={() => toggleFavorite(ex)} data-testid={`exercise-favorite-btn-${ex.id}`}>
                  <Icon name="star" className={`w-4 h-4 ${ex.is_favorite ? 'text-amber-500' : 'text-slate-300'}`} />
                </button>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{ex.short_description || '—'}</p>
              <div className="mt-2.5 flex flex-wrap gap-1 text-xs">
                <span className={`rounded-full px-2 py-0.5 font-semibold ${LEVEL_COLORS[ex.level] || 'bg-slate-100 text-slate-600'}`}>{LEVEL_LABELS[ex.level]}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{ex.duration_minutes ? `${ex.duration_minutes} د` : '—'}</span>
                {ex.is_owner && <span className="rounded-full bg-teal-50 px-2 py-0.5 text-teal-700">{EXERCISE_STATUS_LABELS[ex.status]}</span>}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className="text-slate-400">استُخدم {ex.usage_count} مرة · نُسخ {ex.copy_count} مرة</span>
              </div>
              <div className="mt-3 flex gap-2">
                {ex.is_owner ? (
                  <>
                    <button onClick={() => navigate(`/coach/studio/exercises/${ex.id}`)} className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold hover:border-teal-300" data-testid={`exercise-edit-btn-${ex.id}`}>تعديل</button>
                    {ex.status === 'archived' ? (
                      <button onClick={() => unarchiveExercise(ex.id)} className="rounded-lg border border-teal-200 px-2 py-1.5 text-xs font-semibold text-teal-700" data-testid={`exercise-unarchive-btn-${ex.id}`}>إلغاء الأرشفة</button>
                    ) : (
                      <button onClick={() => archiveExercise(ex.id)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold" data-testid={`exercise-archive-btn-${ex.id}`}>أرشفة</button>
                    )}
                  </>
                ) : (
                  <button onClick={() => doDuplicate(ex.id)} className="flex-1 rounded-lg border border-teal-200 px-2 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-50" data-testid={`exercise-duplicate-btn-${ex.id}`}>نسخ لمكتبتي</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Paginator page={page} total={total} limit={12} onChange={setPage} />
    </div>
  );
}
